// @section 1 Campaigns / Overview — master-level rollup (Adam's "campaign" grain)
// One row per design.campaign_masters (e.g. "CPA & Bookkeeper Q2 2026") plus
// an "Unassigned" row for orphans. Row click → /campaigns/master/:masterKey.
// V14 spine, mirrors MorningMetrics shape.

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  getCampaignMastersOverview,
  getCampaignsFreshness,
  CampaignsUnconfigured,
  type CampaignMastersOverview,
  type CampaignMasterRow,
  type CampaignFreshness,
} from '../../api';

// @section 2 Palette helpers (V14 tokens — locked palette)
const STALE_DAYS = 2;

type SortKey = keyof Pick<
  CampaignMasterRow,
  | 'master_name' | 'sent' | 'bounced' | 'unique_opens' | 'unique_clicks'
  | 'unique_replies' | 'replies_engaged' | 'bookings_count' | 'diag_questionnaire_filled'
  | 'child_campaign_count'
>;

// @section 3 Main
export default function Overview() {
  const navigate = useNavigate();
  const now = new Date();

  const [data, setData] = useState<CampaignMastersOverview | null>(null);
  const [freshness, setFreshness] = useState<CampaignFreshness | null>(null);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [unconfigured, setUnconfigured] = useState(false);

  const [sortKey, setSortKey] = useState<SortKey>('sent');
  const [sortDir, setSortDir] = useState<-1 | 1>(-1);

  // @subsection 3.1 Error router — 503 → unconfigured banner
  const handleErr = (key: string) => (e: unknown) => {
    if (e instanceof CampaignsUnconfigured) { setUnconfigured(true); return; }
    setErrs((p) => ({ ...p, [key]: String(e) }));
  };

  // @subsection 3.2 Initial load
  useEffect(() => {
    getCampaignMastersOverview().then(setData).catch(handleErr('overview'));
    getCampaignsFreshness().then(setFreshness).catch(handleErr('freshness'));
  }, []);

  const totals = data?.totals ?? null;
  const masters = data?.masters ?? null;

  // @subsection 3.3 Sorted masters (client-side; default sent desc — server pre-sorts)
  const sortedMasters = useMemo(() => {
    if (!masters) return [];
    return [...masters].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === 'string' || typeof bv === 'string') {
        return sortDir * String(av ?? '').localeCompare(String(bv ?? ''));
      }
      return sortDir * (((av as number) || 0) - ((bv as number) || 0));
    });
  }, [masters, sortKey, sortDir]);

  const setSort = (k: SortKey) => {
    if (k === sortKey) setSortDir((d) => (d === 1 ? -1 : 1));
    else { setSortKey(k); setSortDir(-1); }
  };

  return (
    <div className="md:-mx-10 md:-my-8">
      <section className="bg-ink-950 text-paper-100 min-h-screen font-sans">
        <div className="px-4 py-5 md:px-10 md:py-7 max-w-[1400px] mx-auto">

          {/* @subsection 3.4 Breadcrumb + live badge */}
          <div className="flex items-center justify-between text-xs mb-6">
            <div className="text-muted">
              <span className="text-paper-100">Atlas</span>
              <span className="opacity-40"> / </span>
              <span className="text-paper-100/80">Campaigns</span>
            </div>
            <div className="flex items-center gap-2 text-[11px] text-ok">
              <span className="w-1.5 h-1.5 rounded-full bg-ok animate-pulse"></span>
              {unconfigured ? 'Pipeline not configured' : 'Live · daily 5 AM AZ refresh'}
            </div>
          </div>

          {/* @subsection 3.5 Title + KPI strip */}
          <div className="flex flex-col md:flex-row items-start gap-4 md:gap-6 mb-8">
            <div className="flex-shrink-0">
              <div className="text-[11px] uppercase tracking-[3px] text-muted mb-2">
                Vertis Group · {now.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}
              </div>
              <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-paper-100 mb-2">Campaigns</h1>
              <p className="text-base text-muted md:whitespace-nowrap">
                {totals
                  ? `${fmt(totals.masters)} campaign${totals.masters === 1 ? '' : 's'} · ${fmt(totals.child_campaigns)} sends · snapshot ${shortTs(totals.taken_at_max)}`
                  : unconfigured
                    ? 'Cold-email campaign engagement — awaiting first refresh'
                    : 'Loading master rollup…'}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:flex-1">
              <Kpi label="Sent" value={totals?.sent} tone="paper-100"
                help={totals ? `${fmt(totals.bounced)} bounced` : '—'} />
              <Kpi label="Unique opens" value={totals?.unique_opens} tone="accent-400"
                help={totals ? `${fmt(totals.unique_clicks)} clicks` : '—'} />
              <Kpi label="Engaged replies" value={totals?.replies_engaged} tone="ok"
                help={totals ? `${fmt(totals.unique_replies)} total replies` : '—'} />
              <Kpi label="Bookings" value={totals?.bookings} tone="watch"
                help={totals ? `${fmt(totals.diag_filled)} diag filled` : '—'} />
            </div>
          </div>

          {/* @subsection 3.6 Sub-view nav strip */}
          <div className="text-xs text-muted flex gap-3 mb-6">
            <span className="text-paper-100 font-semibold">Overview</span>
            <Link to="/campaigns/touch" className="hover:text-paper-100">Touch</Link>
            <Link to="/campaigns/funnel" className="hover:text-paper-100">Funnel</Link>
            <Link to="/campaigns/trend" className="hover:text-paper-100">Trend</Link>
            <Link to="/campaigns/leads" className="hover:text-paper-100">Leads</Link>
            <Link to="/campaigns/replies" className="hover:text-paper-100">Replies</Link>
          </div>

          {/* @subsection 3.7 Unconfigured banner */}
          {unconfigured && (
            <div className="rounded-xl border-2 border-watch/40 bg-ink-900 p-5 text-sm text-watch ring-1 ring-watch/10 mb-6">
              <div className="font-semibold mb-1">Campaigns pipeline isn't configured</div>
              <div className="text-muted">
                Backend returned <code className="text-watch">503 campaigns_unconfigured</code>.
                Once the campaign-engagement pipeline (5 AM AZ daily) has produced its first
                refresh, this page populates automatically.
              </div>
            </div>
          )}

          {/* @subsection 3.8 Body grid — master table left, freshness right */}
          <div className="grid gap-6 grid-cols-1 md:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">

            {/* LEFT — per-master table */}
            <div>
              <div className="flex items-baseline justify-between mb-4">
                <h2 className="text-2xl font-bold text-paper-100">📣 Campaigns</h2>
                <span className="text-xs text-muted">
                  {masters ? `${masters.length} · click a row to drill in` : '—'}
                </span>
              </div>

              {!masters && !errs.overview && !unconfigured && <Skeleton />}
              {errs.overview && <ErrCard msg={errs.overview} />}
              {(unconfigured || (masters && masters.length === 0)) && !errs.overview && (
                <EmptyCard text="No campaigns in the latest snapshot." />
              )}
              {masters && masters.length > 0 && (
                <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-4 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-muted text-left">
                        <Th k="master_name" label="Campaign" cur={sortKey} dir={sortDir} onSort={setSort} />
                        <Th k="child_campaign_count" label="Sends" r cur={sortKey} dir={sortDir} onSort={setSort} />
                        <Th k="sent" label="Sent" r cur={sortKey} dir={sortDir} onSort={setSort} />
                        <Th k="bounced" label="Bounced" r cur={sortKey} dir={sortDir} onSort={setSort} />
                        <Th k="unique_opens" label="Opens" r cur={sortKey} dir={sortDir} onSort={setSort} />
                        <Th k="unique_clicks" label="Clicks" r cur={sortKey} dir={sortDir} onSort={setSort} />
                        <Th k="unique_replies" label="Replies" r cur={sortKey} dir={sortDir} onSort={setSort} />
                        <Th k="replies_engaged" label="Engaged" r cur={sortKey} dir={sortDir} onSort={setSort} />
                        <Th k="diag_questionnaire_filled" label="Diag" r cur={sortKey} dir={sortDir} onSort={setSort} />
                        <Th k="bookings_count" label="Booked" r cur={sortKey} dir={sortDir} onSort={setSort} />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedMasters.map((m) => {
                        const isUnassigned = m.master_key === 'unassigned';
                        return (
                          <tr
                            key={m.master_key}
                            className={`border-t border-ink-800 cursor-pointer hover:bg-ink-800/40 ${isUnassigned ? 'text-muted' : ''}`}
                            onClick={() => navigate(`master/${m.master_key}`)}
                          >
                            <td className="py-1.5">
                              <span className={isUnassigned ? '' : 'text-paper-100'}>{m.master_name}</span>
                              {m.vertical_id && m.vertical_id !== '—' && (
                                <span className="ml-2 text-[10px] text-muted">{m.vertical_id}</span>
                              )}
                              {isUnassigned && (
                                <span className="ml-2 text-[10px] text-watch">⚠ fix in design.campaigns</span>
                              )}
                            </td>
                            <td className="text-right tabular-nums">{fmt(m.child_campaign_count)}</td>
                            <td className="text-right tabular-nums">{fmt(m.sent)}</td>
                            <td className="text-right tabular-nums text-stall">{fmt(m.bounced)}</td>
                            <td className="text-right tabular-nums">{fmt(m.unique_opens)}</td>
                            <td className="text-right tabular-nums">{fmt(m.unique_clicks)}</td>
                            <td className="text-right tabular-nums">{fmt(m.unique_replies)}</td>
                            <td className="text-right tabular-nums text-ok">{fmt(m.replies_engaged)}</td>
                            <td className="text-right tabular-nums">{fmt(m.diag_questionnaire_filled)}</td>
                            <td className="text-right tabular-nums text-watch">{fmt(m.bookings_count)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* RIGHT — data freshness */}
            <div>
              <div className="flex items-baseline justify-between mb-4">
                <h2 className="text-2xl font-bold text-paper-100">📊 Freshness</h2>
                <span className="text-xs text-muted">
                  {freshness?.last_snapshot_at ? `last snapshot ${shortTs(freshness.last_snapshot_at)}` : '—'}
                </span>
              </div>

              <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-4">
                {!freshness && !errs.freshness && !unconfigured && (
                  <div className="text-muted text-xs py-6 text-center">Loading freshness…</div>
                )}
                {errs.freshness && <ErrCard msg={errs.freshness} />}
                {(unconfigured || (freshness && freshness.tables.length === 0)) && !errs.freshness && (
                  <div className="text-muted text-xs py-6 text-center">No refresh records yet.</div>
                )}
                {freshness && freshness.tables.length > 0 && (
                  <div className="space-y-2">
                    {freshness.tables.slice(0, 12).map((f) => {
                      const stale = isStale(f.source_data_max_ts);
                      return (
                        <div key={f.table_name} className="flex items-center justify-between text-xs">
                          <span className="text-paper-100 truncate mr-3" title={f.table_name}>
                            {shortTable(f.table_name)}
                          </span>
                          <span className="flex items-center gap-3 flex-shrink-0 tabular-nums">
                            <span className="text-muted" title={f.refreshed_at}>{rel(f.refreshed_at)}</span>
                            <span className={stale ? 'text-watch' : 'text-muted'}
                                  title={f.source_data_max_ts ?? 'no source max ts'}>
                              src {f.source_data_max_ts ? f.source_data_max_ts.slice(0, 10) : '—'}
                              {stale ? ' ⚠' : ''}
                            </span>
                          </span>
                        </div>
                      );
                    })}
                    <div className="text-[10px] text-muted pt-1">
                      <span className="text-watch">⚠</span> source data older than {STALE_DAYS}d
                    </div>
                  </div>
                )}
              </div>
            </div>

          </div>

        </div>
      </section>
    </div>
  );
}

// @section 4 Presentational helpers (V14)

// @block 4.1 KPI card
function Kpi({ label, value, tone, help }: { label: string; value?: number; tone: string; help: string }) {
  return (
    <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-4">
      <div className="text-[10px] uppercase tracking-[2px] text-muted font-semibold">{label}</div>
      <div className={`text-3xl md:text-4xl font-bold tabular-nums mt-1 text-${tone}`}>
        {value == null ? '—' : fmt(value)}
      </div>
      <div className="text-[11px] text-muted mt-0.5">{help}</div>
    </div>
  );
}

// @block 4.2 Sortable header
function Th<T extends string>({ k, label, r, cur, dir, onSort }: {
  k: T; label: string; r?: boolean; cur: T; dir: -1 | 1; onSort: (k: T) => void;
}) {
  const active = k === cur;
  return (
    <th
      className={`py-1.5 cursor-pointer select-none hover:text-paper-100 ${r ? 'text-right' : ''} ${active ? 'text-paper-100' : ''}`}
      onClick={() => onSort(k)}
    >
      {label}{active ? (dir === -1 ? ' ↓' : ' ↑') : ''}
    </th>
  );
}

// @block 4.3 Loading/error/empty
function Skeleton() {
  return (
    <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-5 animate-pulse">
      <div className="h-4 w-40 bg-ink-800 rounded mb-3"></div>
      <div className="h-3 w-full bg-ink-800/60 rounded mb-2"></div>
      <div className="h-3 w-2/3 bg-ink-800/60 rounded"></div>
    </div>
  );
}
function ErrCard({ msg }: { msg: string }) {
  return <div className="rounded-xl border-2 border-stall/40 bg-ink-900 p-4 text-stall text-xs ring-1 ring-stall/10">{msg}</div>;
}
function EmptyCard({ text }: { text: string }) {
  return (
    <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-6 text-center text-muted text-sm">
      {text}
    </div>
  );
}

// @section 5 Formatting
function fmt(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return n.toLocaleString();
}
function isStale(sourceMaxTs: string | null): boolean {
  if (!sourceMaxTs) return false;
  const t = new Date(sourceMaxTs).getTime();
  if (Number.isNaN(t)) return false;
  return (Date.now() - t) / 86_400_000 > STALE_DAYS;
}
function rel(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const diffMs = Date.now() - t;
  const mins = Math.round(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}
function shortTable(name: string): string {
  const parts = name.split('.');
  return parts.length > 1 ? parts.slice(1).join('.') : name;
}
function shortTs(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}
